import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Card, Badge, Row, Col } from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { FaArrowLeft, FaEye, FaEdit, FaFileInvoice, FaBuilding, FaUser } from 'react-icons/fa';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { API_BASE_URL } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTenant } from '../context/TenantContext';

function SiteCustomers() {
    const { siteId } = useParams();
    const navigate = useNavigate();
    const { currentTenant } = useTenant();
    
    const [customers, setCustomers] = useState([]);
    const [siteInfo, setSiteInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Helper function to create fetch with timeout
    const fetchWithTimeout = useCallback(async (url, options = {}, timeout = 10000) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        
        try {
            const response = await fetchWithAuth(url, {
                ...options,
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            return response;
        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                throw new Error('Request timeout');
            }
            throw error;
        }
    }, []);

    // Fetch site info and customers
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);

            try {
                // Build the URL with optional tenant_id query param
                let url = `${API_BASE_URL}/sites/${siteId}/customers`;
                if (currentTenant?.id) {
                    url += `?tenant_id=${currentTenant.id}`;
                }

                const response = await fetchWithTimeout(url);

                if (!response.ok) {
                    console.error(`HTTP error! Status: ${response.status}`);
                    setError('Failed to load customers');
                    return;
                }

                const data = await response.json();
                
                // Transform the data
                const transformedCustomers = data.customers?.map(customer => ({
                    CustomerID: customer.customer_id,
                    Name: customer.name,
                    Surname: customer.surname,
                    IDNo: customer.id_no,
                    Status: customer.status,
                    tenant_name: customer.tenant_name,
                    is_primary_tenant: customer.is_primary_tenant
                })) || [];
                
                setCustomers(transformedCustomers);
                setSiteInfo({
                    SiteID: data.site_id,
                    SiteName: data.site_name,
                    TenantName: data.tenant_name
                });
            } catch (error) {
                console.error('Error fetching site customers:', error);
                setError('Failed to load customers');
            } finally {
                setIsLoading(false);
            }
        };

        if (siteId) {
            fetchData();
        }
    }, [siteId, currentTenant?.id, fetchWithTimeout]);

    const getStatusIcon = (status) => {
        switch (status?.toUpperCase()) {
            case 'ACTIVE':
                return <span className="text-success">Active</span>;
            case 'INACTIVE':
                return <span className="text-danger">Inactive</span>;
            default:
                return status;
        }
    };

    if (isLoading) {
        return (
            <div className="mt-4">
                <LoadingSpinner
                    size="md"
                    variant="primary"
                    message="Loading site customers..."
                />
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-4">
                <Card>
                    <Card.Body className="text-center">
                        <p className="text-danger">{error}</p>
                        <Button variant="primary" onClick={() => navigate(-1)}>
                            <FaArrowLeft className="mr-2" /> Go Back
                        </Button>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button 
                        variant="link" 
                        onClick={() => navigate(-1)}
                        style={{ padding: 0, marginRight: '10px' }}
                    >
                        <FaArrowLeft style={{ fontSize: '1.5rem', color: '#333' }} />
                    </Button>
                    <span className="ml-2">
                        <FaBuilding className="mr-2" style={{ color: 'var(--theme-accent)' }} />
                        <strong>Site: {siteInfo?.SiteName || 'Unknown Site'}</strong>
                    </span>
                    {siteInfo?.TenantName && (
                        <Badge variant="info" className="ml-2">
                            {siteInfo.TenantName}
                        </Badge>
                    )}
                </div>
            </div>

            {/* Summary Card */}
            <Card className="mb-4">
                <Card.Body>
                    <Row>
                        <Col md={4}>
                            <p className="mb-1"><strong>Site ID:</strong></p>
                            <p className="text-muted">{siteInfo?.SiteID}</p>
                        </Col>
                        <Col md={4}>
                            <p className="mb-1"><strong>Site Name:</strong></p>
                            <p className="text-muted">{siteInfo?.SiteName || 'N/A'}</p>
                        </Col>
                        <Col md={4}>
                            <p className="mb-1"><strong>Total Customers:</strong></p>
                            <p className="text-muted">{customers.length}</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Customers Table */}
            {customers.length === 0 ? (
                <Card>
                    <Card.Body className="text-center">
                        <FaUser className="text-muted mb-2" style={{ fontSize: '2rem' }} />
                        <p>No customers found at this site.</p>
                    </Card.Body>
                </Card>
            ) : (
                <Card>
                    <Card.Header style={{ backgroundColor: 'var(--theme-accent)' }}>
                        <strong>Customers at this Site</strong>
                    </Card.Header>
                    <Card.Body style={{ padding: 0 }}>
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Surname</th>
                                    <th>ID Number</th>
                                    <th>Tenant</th>
                                    <th>Primary</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.map((customer) => (
                                    <tr key={customer.CustomerID}>
                                        <td>{customer.Name}</td>
                                        <td>{customer.Surname}</td>
                                        <td>{customer.IDNo || 'N/A'}</td>
                                        <td>{customer.tenant_name || 'N/A'}</td>
                                        <td>
                                            {customer.is_primary_tenant ? (
                                                <Badge variant="primary">Primary</Badge>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>{getStatusIcon(customer.Status)}</td>
                                        <td>
                                            <Button 
                                                variant="link" 
                                                data-tooltip-id={`view-customer-${customer.CustomerID}`}
                                                as={Link}
                                                to={`/care-recipients`}
                                                onClick={() => {
                                                    // Store the customer ID to navigate to
                                                    sessionStorage.setItem('viewCustomerId', customer.CustomerID);
                                                }}
                                            >
                                                <FaEye style={{ color: '#17a2b8', fontSize: '1.2rem' }} />
                                            </Button>
                                            <ReactTooltip id={`view-customer-${customer.CustomerID}`} place="top" content="View Customer" />
                                            
                                            <Button 
                                                variant="link" 
                                                data-tooltip-id={`edit-customer-${customer.CustomerID}`}
                                                as={Link}
                                                to={`/care-recipients`}
                                                onClick={() => {
                                                    sessionStorage.setItem('editCustomerId', customer.CustomerID);
                                                }}
                                            >
                                                <FaEdit style={{ color: '#007bff', fontSize: '1.2rem' }} />
                                            </Button>
                                            <ReactTooltip id={`edit-customer-${customer.CustomerID}`} place="top" content="Edit Customer" />
                                            
                                            <Button 
                                                variant="link" 
                                                data-tooltip-id={`quotations-${customer.CustomerID}`}
                                                as={Link}
                                                to={`/quotations/${customer.CustomerID}`}
                                            >
                                                <FaFileInvoice style={{ color: '#6c757d', fontSize: '1.2rem' }} />
                                            </Button>
                                            <ReactTooltip id={`quotations-${customer.CustomerID}`} place="top" content="View Quotations" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
}

export default SiteCustomers;